<template>
  <div class="user-verify-wrapper">
    <div class="verify-card">
      <!-- 加载状态 -->
      <div v-if="loading" class="status-container loading">
        <svg class="loading-icon" viewBox="0 0 50 50">
          <circle cx="25" cy="25" r="20" fill="none" stroke-width="5" stroke-linecap="round" class="path"></circle>
        </svg>
        <span>正在加载</span>
      </div>
      
      <!-- 错误状态 -->
      <div v-else-if="error" class="status-container error">
        <svg class="error-icon" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        <span>{{ error }}</span>
        <div class="retry-button" @click="resetForm">
          <span>重试</span>
        </div>
      </div>
      
      <!-- 成功状态 -->
      <div v-else-if="verifySuccess" class="status-container success">
        <svg class="success-icon" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
        <span>验证成功</span>
      </div>
      
      <!-- 表单容器 -->
      <div v-else class="form-container">
        <div class="form-title">
          <span>用户验证</span>
        </div>
        
        <div class="form-content">
          <!-- 手机号输入 -->
          <div class="input-group">
            <div class="input-label">手机号</div>
            <input
              type="tel"
              v-model="phone"
              class="input-field"
              placeholder="请输入手机号"
              maxlength="11"
              @input="validatePhoneInput"
            />
          </div>
          
          <!-- 如果需要额外信息 -->
          <template v-if="needExtraInfo">
            <!-- 姓名输入 -->
            <div class="input-group">
              <div class="input-label">姓名</div>
              <input
                type="text"
                v-model="name"
                class="input-field"
                placeholder="请输入姓名"
                maxlength="20"
              />
            </div>
            
            <!-- 身份证输入 -->
            <div class="input-group">
              <div class="input-label">身份证号</div>
              <input
                type="text"
                v-model="idcard"
                class="input-field"
                placeholder="请输入身份证号"
                maxlength="18"
                @input="validateIdCardInput"
              />
            </div>
          </template>
          
          <!-- 提交按钮 -->
          <div class="submit-button" :class="{ 'disabled': !isFormValid }" @click="submitForm">
            <span>提交</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { validateUserPhone } from '@/api/api'

export default {
  name: 'UserVerify',
  data() {
    return {
      loading: false,
      error: null,
      phone: '',
      name: '',
      idcard: '',
      needExtraInfo: false,
      verifySuccess: false
    }
  },
  mounted() {
    // 添加监听刷新事件
    window.addEventListener('keydown', this.handleKeyDown)
    window.addEventListener('popstate', this.handleRouteChange)
  },
  beforeDestroy() {
    // 移除监听
    window.removeEventListener('keydown', this.handleKeyDown)
    window.removeEventListener('popstate', this.handleRouteChange)
  },
  beforeRouteUpdate(to, from, next) {
    // 路由参数变化时触发重新加载
    if (to.params.payOrderId !== from.params.payOrderId) {
      this.resetForm()
    }
    next()
  },
  computed: {
    // 表单验证
    isFormValid() {
      // 手机号必须是11位数字
      const isPhoneValid = /^1\d{10}$/.test(this.phone);
      
      if (!isPhoneValid) return false;
      
      // 如果需要额外信息，则验证姓名和身份证
      if (this.needExtraInfo) {
        // 姓名不能为空
        const isNameValid = this.name.trim().length > 0;
        // 身份证号必须是15位或18位
        const isIdCardValid = /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/.test(this.idcard);
        
        return isPhoneValid && isNameValid && isIdCardValid;
      }
      
      return isPhoneValid;
    }
  },
  methods: {
    // 处理路由变化
    handleRouteChange() {
      this.resetForm()
    },
    
    // 处理键盘按键
    handleKeyDown(event) {
      // 处理Enter键
      if (event.key === 'Enter') {
        // 只在表单未成功提交时重置
        if (!this.verifySuccess) {
          // 若表单已填写但未提交，则触发提交
          if (this.isFormValid) {
            this.submitForm();
          } else {
            // 若表单未填写或无效，则只重置错误状态
            this.error = null;
          }
        }
        // 阻止默认行为（例如表单提交）
        event.preventDefault();
      }
    },
    
    // 重置表单并刷新页面状态
    resetForm() {
      this.loading = false;
      this.error = null;
      this.phone = '';
      this.name = '';
      this.idcard = '';
      this.needExtraInfo = false;
      this.verifySuccess = false;
      
      // 可选：强制页面重新渲染
      this.$forceUpdate();
      
      // 输出调试信息
      console.log('表单已重置, 路径:', this.$route.path, '参数:', this.$route.params);
    },
    
    // 验证手机号输入，只允许数字
    validatePhoneInput() {
      this.phone = this.phone.replace(/\D/g, '');
    },
    
    // 验证身份证输入，只允许数字和最后一位的X
    validateIdCardInput() {
      this.idcard = this.idcard.replace(/[^\dXx]/g, '');
      // 转换小写x为大写X
      this.idcard = this.idcard.replace(/x$/, 'X');
    },
    
    // 提交表单
    submitForm() {
      if (!this.isFormValid) return;
      
      this.loading = true;
      this.error = null;
      
      const payOrderId = this.$route.params.payOrderId;
      if (!payOrderId) {
        this.error = '订单ID不存在';
        this.loading = false;
        return;
      }
      
      // 调用API验证用户
      validateUserPhone(
        payOrderId, 
        this.phone, 
        this.needExtraInfo ? this.name : null, 
        this.needExtraInfo ? this.idcard : null
      )
        .then(res => {
          this.loading = false;
          
          // 调试信息
          console.log('API响应:', JSON.stringify(res));
          
          // HttpRequest.js中的拦截器会在code===0时直接返回data部分
          // 因此，如果收到的是对象且包含payOrderId，表示验证成功
          if (res && res.payOrderId) {
            // 验证成功
            this.verifySuccess = true;
            
            // 如果返回了redirectUrl，延迟后跳转到指定URL
            if (res.redirectUrl) {
              setTimeout(() => {
                window.location.href = res.redirectUrl;
              }, 1500);
            } else {
              // 否则跳转到订单详情页
              setTimeout(() => {
                this.$router.push({ name: 'OrderDetail', params: { payOrderId } });
              }, 1500);
            }
          } else {
            // 由于成功情况返回的是data部分，这里表示有错误
            // 但实际上我们不应该进入这个分支，因为错误会进入catch
            this.error = '验证失败，请重试';
          }
        })
        .catch(err => {
          console.error('验证用户失败:', err);
          this.loading = false;
          
          if (typeof err === 'string') {
            this.error = err;
          } else if (err && err.msg) {
            // 检查是否是"用户不存在"的情况
            if (err.msg === '用户不存在') {
              this.needExtraInfo = true;
              this.error = null;
            } else {
              this.error = err.msg;
            }
          } else {
            this.error = '验证失败，请重试';
          }
        });
    }
  }
}
</script>

<style scoped>
.user-verify-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 100vh;
  background-color: #fafafa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif;
  padding: 5px;
  box-sizing: border-box;
  overflow: visible;
}

.verify-card {
  width: 100%;
  max-width: 360px;
  min-height: auto;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  overflow: visible;
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 5px 0;
}

/* 状态容器样式 */
.status-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 30px 20px;
  text-align: center;
}

.status-container span {
  margin-top: 16px;
  font-size: 16px;
  color: #333;
}

/* 加载状态 */
.loading-icon {
  width: 40px;
  height: 40px;
  animation: loading 1.5s linear infinite;
}

.loading-icon .path {
  stroke: #1890ff;
  stroke-dasharray: 90, 150;
  stroke-dashoffset: 0;
  stroke-linecap: round;
}

@keyframes loading {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 错误状态 */
.error-icon {
  width: 40px;
  height: 40px;
  fill: #ff4d4f;
}

.retry-button {
  margin-top: 16px;
  padding: 8px 16px;
  background-color: #f5f5f5;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.retry-button:hover {
  background-color: #e8e8e8;
}

/* 成功状态 */
.success-icon {
  width: 48px;
  height: 48px;
  fill: #52c41a;
}

/* 表单容器 */
.form-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
}

.form-title {
  padding: 12px 5px;
  text-align: center;
  font-size: 16px;
  color: #333;
  background-color: #f9f9f9;
  border-bottom: 1px solid #f0f0f0;
}

.form-content {
  padding: 25px;
}

.input-group {
  margin-bottom: 20px;
}

.input-label {
  font-size: 15px;
  color: #666;
  margin-bottom: 8px;
}

.input-field {
  width: 100%;
  height: 44px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 0 15px;
  font-size: 16px;
  box-sizing: border-box;
  transition: all 0.3s;
}

.input-field:focus {
  border-color: #1890ff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.submit-button {
  margin-top: 24px;
  height: 46px;
  background-color: #1890ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.submit-button:hover {
  background-color: #40a9ff;
}

.submit-button.disabled {
  background-color: #d9d9d9;
  cursor: not-allowed;
}

/* 响应式调整 */
@media (max-width: 480px) {
  .user-verify-wrapper {
    padding: 15px;
    align-items: flex-start;
    min-height: calc(100vh - 30px);
  }
  
  .verify-card {
    margin: 10px 0;
    width: 100%;
    max-width: 100%;
  }
  
  .form-title {
    padding: 14px 5px;
    font-size: 17px;
  }
  
  .form-content {
    padding: 20px 15px;
  }
  
  .input-group {
    margin-bottom: 22px;
  }
  
  .input-label {
    font-size: 16px;
    margin-bottom: 10px;
  }
  
  .input-field {
    height: 48px;
    font-size: 16px;
    border-radius: 8px;
  }
  
  .submit-button {
    height: 50px;
    font-size: 17px;
    border-radius: 8px;
    margin-top: 28px;
  }
  
  .status-container {
    min-height: 350px;
  }
  
  .status-container span {
    font-size: 17px;
    margin-top: 20px;
  }
  
  .error-icon, .success-icon {
    width: 50px;
    height: 50px;
  }
  
  .retry-button {
    padding: 10px 20px;
    font-size: 16px;
    margin-top: 20px;
  }
}

/* 支持更小屏幕的设备 */
@media (max-height: 600px) {
  .verify-card {
    min-height: auto;
  }
  
  .status-container {
    min-height: auto;
    padding: 25px 20px;
  }
  
  .form-content {
    padding: 15px;
  }
  
  .input-group {
    margin-bottom: 16px;
  }
}

/* 增加小屏手机的特殊适配 */
@media (max-width: 360px) {
  .user-verify-wrapper {
    padding: 10px;
  }
  
  .form-content {
    padding: 15px 12px;
  }
  
  .input-field {
    height: 46px;
  }
}

/* 增加针对超小屏的适配 */
@media (max-width: 320px) {
  .form-title {
    padding: 12px 5px;
    font-size: 16px;
  }
  
  .input-label {
    font-size: 15px;
  }
  
  .input-field {
    height: 44px;
    font-size: 15px;
  }
  
  .submit-button {
    height: 46px;
    font-size: 16px;
  }
}
</style> 